import { env } from "@/env";
import { StructuredData } from "@utilities/structuredData";
import { BaseLink } from "@components/core/Link";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";

export function BlockBreadcrumbsV2({
  links,
  size = "default",
  inverse = false,
  hidenOnMobileAfter = 1,
}: {
  links?: { href?: null | string; title: string }[] | null;
  size?: "default" | "small";
  inverse?: boolean;
  hidenOnMobileAfter?: number;
}) {
  const t = useTranslations("ClientKeys.Breadcrumbs");

  if (!links?.length) {
    return null;
  }

  const linksWithHref = links.filter(link => !!link.href);
  const Container = linksWithHref.length > 0 ? "nav" : "div";

  return (
    <>
      {linksWithHref.length > 0 && (
        <StructuredData
          id='breadcrumbs'
          data={[
            {
              "@type": "BreadcrumbList",
              itemListElement: linksWithHref.map((link, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: link.title,
                item: env.NEXT_PUBLIC_SERVER_URL + link.href,
              })),
            },
          ]}
        />
      )}
      <Container aria-label={Container === "nav" ? t("label") : undefined}>
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ol
          role='list'
          className={clsx("tw-flex tw-flex-wrap tw-gap-y-2.5", size === "small" ? "tw-gap-x-1.5" : "tw-gap-x-2")}
        >
          {links.map((link, index) => {
            const isFirst = index === 0;
            const shouldHideOnMobile = index > hidenOnMobileAfter;

            const component = link.href ? (
              <BaseLink
                href={link.href}
                className={clsx(
                  "tw-block tw-transition-colors tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm focus-visible:tw-outline-offset-4",
                  inverse ? "tw-text-gray-300 hover:tw-text-white" : "tw-text-gray-500 hover:tw-text-black",
                  size === "small" ? "tw-type-sm" : "tw-type-base",
                )}
              >
                {link.title}
              </BaseLink>
            ) : (
              <span
                key={index}
                className={clsx(
                  "tw-block",
                  inverse ? "tw-text-white" : "tw-text-black",
                  size === "small" ? "tw-type-sm" : "tw-type-base",
                )}
              >
                {link.title}
              </span>
            );

            return (
              <li
                key={index}
                className={clsx(
                  shouldHideOnMobile
                    ? [
                        "tw-sr-only sm:tw-not-sr-only sm:tw-flex sm:tw-relative",
                        !isFirst && [size === "small" ? "sm:tw-pl-3" : "sm:tw-pl-3.5"],
                      ]
                    : ["tw-flex tw-relative", !isFirst && [size === "small" ? "tw-pl-3" : "tw-pl-3.5"]],
                )}
              >
                {!isFirst &&
                  (size === "small" ? (
                    <svg
                      viewBox='0 0 5 12'
                      fill='none'
                      aria-hidden
                      className={clsx(
                        "tw-h-3 tw-absolute tw-left-0 tw-top-0",
                        inverse ? "tw-text-gray-500" : "tw-text-gray-300",
                      )}
                    >
                      <path stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' d='M1 11 4 1' />
                    </svg>
                  ) : (
                    <svg
                      viewBox='0 0 6 13'
                      fill='none'
                      aria-hidden
                      className={clsx(
                        "tw-w-1.5 tw-absolute tw-left-0 -tw-top-0.125",
                        inverse ? "tw-text-gray-500" : "tw-text-gray-300",
                      )}
                    >
                      <path stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' d='M1 12 5 1' />
                    </svg>
                  ))}
                {component}
              </li>
            );
          })}
        </ol>
      </Container>
    </>
  );
}
